// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * LES GUIDES DE RECUEIL — une bibliothèque paramétrable, rattachée aux examens
 * (RQ, 13/09/2026).
 *
 * ── Ce qui a changé ────────────────────────────────────────────────────────
 *
 * Les guides (ECBU, coproculture, PV, expectoration, sperme, gorge) étaient
 * écrits en dur dans le manuel (`SAMPLE_GUIDES`) et rattachés aux examens par
 * une RÈGLE : le récipient de l'examen, plus des mots-clés dans son libellé. La
 * règle se trompait dans les deux sens, constaté en base le 13/09 :
 *   · le SPERMOGRAMME n'a aucun récipient renseigné → aucun guide, alors que
 *     c'est l'examen où le recueil compte le plus ;
 *   · la CALPROTECTINE et la recherche de sang dans les selles recevaient le
 *     guide de COPROCULTURE, qui n'a pas de mot-clé.
 *
 * Désormais la RQ règle les guides dans Qualité › Référentiel › Paramétrage ›
 * Recueil, et COCHE les examens auxquels chacun s'attache. Le lien est une liste
 * de codes, lisible et vérifiable — plus une heuristique.
 *
 * ── Tant que rien n'est enregistré ─────────────────────────────────────────
 *
 * `field-config.guidesRecueil` vaut `null` : on sert les guides historiques,
 * avec les corrections éditoriales déjà faites dans le manuel (`manualContent`,
 * clés `FR.guide.<id>.<champ>`), et des rattachements CALCULÉS par l'ancienne
 * règle — plus le spermogramme, rattaché par son code. Rien ne disparaît de
 * l'écran le jour de la bascule ; la RQ part de ce qu'elle voit.
 *
 * Module PUR : aucun import d'application. Il vit dans `shared/ui/` pour que le
 * manuel (qui affiche) et Qualité (qui règle) ne puissent pas diverger.
 */

export type IconeGuide = 'drop' | 'flask' | 'swab' | 'lungs';

export interface GuideRecueil {
  /** Identifiant stable (minuscules, chiffres, tirets) — jamais réattribué. */
  id: string;
  titre: string;
  sousTitre: string;
  /** Couleur de la pastille, `#rrggbb`. */
  couleur: string;
  icone: IconeGuide;
  preparation: string;
  recueil: string;
  recipient: string;
  acheminement: string;
  /** Point de vigilance, affiché en encart ambré. Vide = pas d'encart. */
  points: string;
  /** Codes des examens auxquels ce guide s'attache. */
  examens: string[];
  /** Un guide inactif est conservé mais ne s'affiche nulle part. */
  actif: boolean;
}

/** Ce qu'il faut d'un examen pour calculer les rattachements par défaut. */
export interface ExamenPourGuide {
  code: string;
  libelle?: string | null;
  famille?: string | null;
  synonymes?: string[] | null;
  recipient?: string | null;
  echantillons?: { recipient?: string | null }[] | null;
}

export const ICONES_GUIDE: Record<IconeGuide, { libelle: string; trace: string }> = {
  drop: { libelle: 'Goutte', trace: '<path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>' },
  flask: { libelle: 'Flacon', trace: '<path d="M9 3h6"/><path d="M10 3v6.5L5.2 18a2 2 0 0 0 1.7 3h10.2a2 2 0 0 0 1.7-3L14 9.5V3"/>' },
  swab: { libelle: 'Écouvillon', trace: '<path d="M4 20 14 10"/><circle cx="17" cy="7" r="3"/>' },
  lungs: { libelle: 'Poumons', trace: '<path d="M12 4v8"/><path d="M8 8c-3 0-5 3-5 7v3a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V8"/><path d="M16 8c3 0 5 3 5 7v3a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V8"/>' },
};

/** Les bornes, identiques à celles du moteur (qui reste l'autorité). */
export const BORNES_GUIDES = { guides: 50, texte: 1500, titre: 120, examens: 400 } as const;

/** Les guides historiques, et la règle qui les rattachait. `motsCles` et
 *  `recipients` ne servent QU'AU calcul des rattachements par défaut. */
const HISTORIQUES: (Omit<GuideRecueil, 'examens' | 'actif'> & { recipients: string[]; motsCles?: string[]; codes?: string[] })[] = [
  {
    id: 'ecbu', recipients: ['urine'], motsCles: ['ecbu', 'cytobacteriolog', 'uroculture', 'urocult'],
    titre: 'ECBU — Examen cytobactériologique des urines', sousTitre: 'Urine du milieu de jet', couleur: '#38BDF8', icone: 'drop',
    preparation: 'Toilette périnéale soigneuse au préalable. 1res urines du matin de préférence, ou à plus de 4 h de la dernière miction.',
    recueil: 'Éliminer le premier jet, recueillir le milieu de jet directement dans le flacon stérile sans toucher le bord.',
    recipient: 'Flacon stérile à usage unique (± milieu conservateur si acheminement différé).',
    acheminement: 'Ambiante < 2 h, ou réfrigéré 2–8 °C < 24 h. Préciser un sondage éventuel.',
    points: 'Ne pas prélever en cours d’antibiothérapie sauf avis contraire. Identifier le flacon immédiatement.',
  },
  {
    id: 'copro', recipients: ['selles'], motsCles: ['copro', 'culture de selles', 'bacteriolog'],
    titre: 'Coproculture', sousTitre: 'Selles fraîches', couleur: '#92633A', icone: 'flask',
    preparation: 'Avant toute antibiothérapie. Recueil dans un pot propre et sec.',
    recueil: 'Prélever l’équivalent d’une noix de selles (zones glaireuses/sanglantes en priorité). Ni urine ni eau dans le pot.',
    recipient: 'Pot à selles stérile (± écouvillon rectal en milieu de transport).',
    acheminement: 'Ambiante < 2 h, ou réfrigéré < 12 h. Préciser voyage récent / contexte épidémique.',
    points: '',
  },
  {
    id: 'pv', recipients: ['ecouvillon'], motsCles: ['vaginal', 'vaginaux', 'genital', 'endocol', 'cervic', 'pv'],
    titre: 'Prélèvement vaginal (PV)', sousTitre: 'Écouvillonnage', couleur: '#EC4899', icone: 'swab',
    preparation: 'Pas de toilette intime ni d’ovule la veille. À distance des règles.',
    recueil: 'Écouvillonner les parois vaginales (± endocol selon prescription). Deux écouvillons recommandés.',
    recipient: 'Écouvillon avec milieu de transport (type Amies / eSwab).',
    acheminement: 'Ambiante < 2 h. Préciser grossesse et signes cliniques.',
    points: 'Acheminement rapide impératif pour la recherche de germes fragiles.',
  },
  {
    id: 'ecbc', recipients: ['expecto'], motsCles: ['expectoration', 'crachat', 'ecbc', 'bk', 'bronch'],
    titre: 'Expectoration (ECBC)', sousTitre: 'Crachat profond', couleur: '#0D9488', icone: 'lungs',
    preparation: 'Le matin à jeun, après rinçage de la bouche à l’eau. Avant antibiothérapie.',
    recueil: 'Effort de toux profond pour ramener une expectoration (et non de la salive) dans le flacon.',
    recipient: 'Flacon stérile à large ouverture.',
    acheminement: 'Réfrigéré < 2 h. Préciser kinésithérapie / aérosol éventuel.',
    points: 'Un échantillon salivaire est rejeté : privilégier un crachat profond.',
  },
  {
    // Le spermogramme n'a pas de récipient en base : il est rattaché par son CODE.
    id: 'sperme', recipients: ['sterile'], motsCles: ['sperm'], codes: ['SPERMOG', 'SPERMOG_TMS'],
    titre: 'Spermogramme / spermoculture', sousTitre: 'Recueil par masturbation', couleur: '#7E3FF2', icone: 'flask',
    preparation: 'Abstinence de 2 à 5 jours. Recueil au laboratoire de préférence.',
    recueil: 'Recueil par masturbation après miction et lavage des mains et du gland, dans le flacon stérile.',
    recipient: 'Flacon stérile à usage unique, identifié.',
    acheminement: 'À 37 °C (près du corps), acheminement immédiat < 30 min.',
    points: 'Noter l’heure de recueil et la durée d’abstinence sur le flacon.',
  },
  {
    id: 'gorge', recipients: ['ecouvillon'], motsCles: ['gorge', 'pharyng', 'angine', 'amygdal', 'strepto'],
    titre: 'Prélèvement de gorge', sousTitre: 'Écouvillonnage pharyngé', couleur: '#16A34A', icone: 'swab',
    preparation: 'À distance d’un bain de bouche antiseptique. Avant antibiothérapie.',
    recueil: 'Écouvillonner les amygdales et le pharynx postérieur en évitant la langue et la salive.',
    recipient: 'Écouvillon avec milieu de transport.',
    acheminement: 'Ambiante < 2 h. Préciser le contexte (angine, portage…).',
    points: '',
  },
];

const normaliser = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Les anciennes clés éditoriales du manuel, par champ du guide. */
const CLES_MANUEL: Record<string, keyof GuideRecueil> = {
  title: 'titre', sub: 'sousTitre', prep: 'preparation', recueil: 'recueil', recipient: 'recipient', conservation: 'acheminement', points: 'points',
};

/**
 * Les guides par défaut, tels que le manuel les AFFICHE aujourd'hui : textes
 * historiques, corrections éditoriales françaises appliquées, rattachements
 * calculés par l'ancienne règle sur la liste d'examens fournie.
 *
 * ⚠️ Correction de la règle au passage : la coproculture exige désormais un
 * mot-clé — sans lui, tout examen sur pot à selles (calprotectine…) recevait
 * une consigne de culture qui ne le concerne pas.
 */
export function guidesParDefaut(examens: ExamenPourGuide[], manualContent?: Record<string, string> | null): GuideRecueil[] {
  const mc = manualContent || {};
  return HISTORIQUES.map((h) => {
    const { recipients, motsCles, codes, ...g } = h;
    const guide: GuideRecueil = { ...g, examens: [], actif: true };
    for (const [cle, champ] of Object.entries(CLES_MANUEL)) {
      const v = mc[`FR.guide.${h.id}.${cle}`];
      if (typeof v === 'string' && v.trim()) (guide as unknown as Record<string, unknown>)[champ] = v;
    }
    const retenus = new Set<string>(codes || []);
    for (const e of examens) {
      const rec = new Set<string>([e.recipient, ...(e.echantillons || []).map((x) => x?.recipient)].filter(Boolean) as string[]);
      if (!recipients.some((r) => rec.has(r))) continue;
      const texte = normaliser([e.libelle, ...(e.synonymes || []), e.famille].filter(Boolean).join(' '));
      if (!motsCles?.length || motsCles.some((m) => texte.includes(normaliser(m)))) retenus.add(e.code);
    }
    // Un code historique absent du catalogue fourni ne se rattache pas dans le vide.
    guide.examens = [...retenus].filter((c) => examens.some((e) => e.code === c)).sort();
    return guide;
  });
}

/** Les guides en vigueur : le paramétrage s'il existe (même vide — une liste
 *  vide est un choix), sinon les guides par défaut. */
export function guidesEnVigueur(parametres: unknown, examens: ExamenPourGuide[], manualContent?: Record<string, string> | null): GuideRecueil[] {
  return Array.isArray(parametres) ? (parametres as GuideRecueil[]) : guidesParDefaut(examens, manualContent);
}

/** Les guides ACTIFS rattachés à un examen, dans l'ordre de la bibliothèque. */
export function guidesDeLExamen(guides: GuideRecueil[], code: string): GuideRecueil[] {
  return guides.filter((g) => g.actif !== false && Array.isArray(g.examens) && g.examens.includes(code));
}

/** Contrôle d'une bibliothèque avant écriture. Rend la PREMIÈRE erreur, ou
 *  `null`. Le moteur applique les mêmes bornes — ce contrôle-ci ne sert qu'à
 *  prévenir à l'écran. */
export function erreurGuides(guides: unknown): string | null {
  if (!Array.isArray(guides)) return 'La bibliothèque doit être une liste.';
  if (guides.length > BORNES_GUIDES.guides) return `${BORNES_GUIDES.guides} guides au plus.`;
  const ids = new Set<string>();
  for (const g of guides as Partial<GuideRecueil>[]) {
    const nom = g?.titre || g?.id || '(sans titre)';
    if (!g || typeof g !== 'object') return 'Guide mal formé.';
    if (!/^[a-z0-9-]{1,40}$/.test(String(g.id || ''))) return `« ${nom} » : identifiant invalide (minuscules, chiffres, tirets).`;
    if (ids.has(String(g.id))) return `Identifiant en double : ${g.id}.`;
    ids.add(String(g.id));
    if (!String(g.titre || '').trim()) return 'Un guide doit avoir un titre.';
    if (String(g.titre).length > BORNES_GUIDES.titre) return `« ${nom} » : titre trop long.`;
    if (!/^#[0-9a-fA-F]{6}$/.test(String(g.couleur || ''))) return `« ${nom} » : couleur invalide.`;
    if (!(String(g.icone) in ICONES_GUIDE)) return `« ${nom} » : icône inconnue.`;
    for (const champ of ['sousTitre', 'preparation', 'recueil', 'recipient', 'acheminement', 'points'] as const) {
      if (typeof g[champ] !== 'string') return `« ${nom} » : champ « ${champ} » manquant.`;
      if ((g[champ] as string).length > BORNES_GUIDES.texte) return `« ${nom} » : « ${champ} » trop long.`;
    }
    if (!Array.isArray(g.examens) || g.examens.some((c) => typeof c !== 'string' || !c.trim())) return `« ${nom} » : liste d'examens invalide.`;
    if (g.examens.length > BORNES_GUIDES.examens) return `« ${nom} » : trop d'examens rattachés.`;
  }
  return null;
}
